/**
 * LoginPage — T21.
 *
 * Centered card with email + password form. On success navigates to the
 * originally requested path (stored in router location state) or to /.
 *
 * Error messages (verbatim per PRD §10 UX requirements):
 *   401                        → "Invalid email or password."
 *   503 with code 'no_users'   → "The system is not yet configured. Contact an administrator."
 *   Other ApiError             → error.message
 */
import { Alert, Button, Card, Center, PasswordInput, Stack, TextInput, Title } from '@mantine/core';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/errors';
import { useAuthStore } from '../state/auth-store';

// ─── Error message resolution ─────────────────────────────────────────────────

function resolveErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return 'Invalid email or password.';
    }
    if (err.status === 503 && err.code === 'no_users') {
      return 'The system is not yet configured. Contact an administrator.';
    }
    return err.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

// ─── LoginPage ────────────────────────────────────────────────────────────────

export default function LoginPage() {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Navigate to the page the user originally tried to access, or to /
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setErrorMsg(resolveErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Center h="100vh" data-testid="login-page">
      <Card shadow="sm" padding="xl" radius="md" withBorder w={380} data-testid="login-card">
        <Stack gap="md">
          <Title order={2} ta="center">
            Sign in
          </Title>

          {errorMsg && (
            <Alert color="red" data-testid="login-error">
              {errorMsg}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <Stack gap="sm">
              <TextInput
                id="login-email"
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
                autoComplete="email"
                data-testid="login-email"
              />

              <PasswordInput
                id="login-password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={loading}
                mt="sm"
                data-testid="login-submit"
              >
                Sign in
              </Button>
            </Stack>
          </form>
        </Stack>
      </Card>
    </Center>
  );
}
